import { useCallback, useEffect, useMemo, useState } from "react";
import { journalApi } from "../services/journalApi";
import { useToast } from "../context/ToastContext";
import { useAccount } from "../context/AccountContext";
import { Button } from "../components/common/Button";
import { Card } from "../components/common/Card";
import { ConfirmDialog } from "../components/common/ConfirmDialog";
import { EmptyState } from "../components/common/EmptyState";
import { Field } from "../components/common/Field";
import { Badge } from "../components/common/Badge";
import { CharacterCount } from "../components/common/CharacterCount";
import { JournalFilters } from "../components/journal/JournalFilters";
import { JournalTagManager } from "../components/journal/JournalTagManager";
import { CollapsibleBody } from "../components/journal/CollapsibleBody";
import { CollapsibleChips, JOURNAL_CHIP_LIMIT } from "../components/journal/CollapsibleChips";
import { toggleCheckbox } from "../services/journalMarkdown";
import { JOURNAL_COLOURS, colourOf } from "../services/journalColours";
import { JOURNAL_BODY_MAX } from "../config/limits";
import { formatDateOnly } from "../services/hiveActions";

const NO_FILTERS = {
  search: "", colour: null, tagIds: [], authorIds: [], pinned: false, dateFrom: "", dateTo: "",
};

/**
 * The beekeeper's journal (#265).
 *
 * One diary with a visibility flag, not two. An entry is private when
 * written, can be shared with the team, and can be pulled back -- which
 * only makes sense because sharing moves a flag rather than making a
 * copy.
 *
 * The thing this screen has to be honest about is what pulling back
 * CANNOT do. It removes an entry from the team view; it cannot unsee what
 * a colleague already read. Saying so at the moment somebody shares is
 * the difference between a feature and a false promise.
 */

const TABS = [
  { key: "mine", label: "My journal" },
  { key: "team", label: "Shared with the team" },
];

export function JournalPage() {
  const { notify } = useToast();
  /* WHOSE APIARY THIS IS (#471). A new entry is filed under the
     account being worked in, and nothing on this screen said so --
     so a helper wrote notes into an apiary they could not name,
     and "share with the team" reached a team they could not see.
     hasChoice is false for anyone who only has their own, and
     then none of this appears. */
  const { currentLabel, hasChoice } = useAccount();
  const [scope, setScope] = useState("mine");
  // Entries awaiting a confirmation, or null (#328).
  const [pendingDelete, setPendingDelete] = useState(null);
  const [pendingShare, setPendingShare] = useState(null);
  const [entries, setEntries] = useState(null);
  const [draft, setDraft] = useState("");
  // Colour and tags chosen while writing (#498). Applied right after the
  // entry exists, so there is no "find it again and tag it" step.
  const [draftColour, setDraftColour] = useState(null);
  const [draftTagIds, setDraftTagIds] = useState([]);
  const [saving, setSaving] = useState(false);
  /* ONE entry edits at a time. editingId is the whole lock: every other
     card's Edit is disabled while this is set, so an in-progress edit
     cannot be discarded by clicking Edit somewhere else. Losing typed
     text to a stray click is the kind of small betrayal that stops
     people trusting a journal with anything long. */
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  /* Filters start EMPTY every time (#464). A journal opened tomorrow
     shows the journal, not yesterday's filter with three entries in it
     and no obvious reason why -- which reads as data loss, and on a
     private diary that is an alarming thing to read. */
  const [filters, setFilters] = useState(NO_FILTERS);
  const [tags, setTags] = useState([]);
  const [managingTags, setManagingTags] = useState(false);
  /* HELD HERE, NOT IN THE CARD (#473). The list reloads after a
     tag, colour or pin change, so state owned by the body would
     survive a re-render and not a remount -- an entry somebody
     expanded would collapse underneath them when they ticked a
     tag, which is confusing because nothing they did was about
     the text. */
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  /* Tags met on entries this session, accumulating. See filterableTags
     below for why this is remembered rather than derived. */
  const [discovered, setDiscovered] = useState([]);
  /* People met as authors of shared entries, accumulating for the same
     reason as tags: narrowing to one author must not remove the others
     from the picker (#499). Only people who have actually written into
     this view ever appear -- never the share list. */
  const [authors, setAuthors] = useState([]);

  const filtersActive = Boolean(
    filters.search || filters.colour || filters.tagIds.length || filters.authorIds.length ||
    filters.pinned || filters.dateFrom || filters.dateTo);

  /* FILTERABLE BY WHAT YOU CAN SEE (#477).
   *
   * The filter bar offered `tags` -- list_tags, which is author-scoped
   * on purpose. So once V119 made a teammate's tags VISIBLE on a shared
   * entry, you could read a tag and not filter by it, which is the half
   * of the original objection that stayed broken:
   *
   *     "If the users can't tag entries then the other team members
   *      can't filter or search on those tags in the shared entries."
   *
   * The server already supports it -- list_entries' tag_ids filter goes
   * through app.JournalEntryTags, whose predicate V119 loosened -- so
   * this was only ever the client not offering the term.
   *
   * YOUR OWN TAGS COME FIRST AND ALWAYS, even with nothing loaded: they
   * are your vocabulary and must not appear and disappear as the list
   * changes underneath a filter.
   *
   * BOUNDED BY WHAT IS LOADED, and that is a real limit worth stating
   * rather than hiding. A teammate's tag becomes offerable once an
   * entry carrying it is on screen. The alternative is an endpoint
   * returning every tag on every entry you can reach, which is a second
   * source for the same list and the kind of thing that drifts -- and
   * the common case, filtering a list you are looking at, is served
   * exactly by this.
   *
   * MANAGE TAGS STILL GETS `tags` ALONE. Retiring and renaming belong to
   * the author, and offering somebody else's label in your own manage
   * list would be a control that refuses -- which is the reasoning
   * list_tags' author filter already records.
   *
   * ── WHY DISCOVERED TAGS ACCUMULATE RATHER THAN BEING DERIVED ──
   *
   * Deriving the list straight from the entries on screen has a hole:
   * filter by a teammate's tag, add a date range that matches nothing,
   * and the entries go empty -- so the tag drops out of the options and
   * its own filter chip disappears while STILL APPLIED. The list is then
   * narrowed by something with no visible control to turn off, which
   * reads as the journal having lost entries. CollapsibleChips' "a
   * selected chip is never hidden" rule exists for the same failure, and
   * JournalPage already guards the retired-tag version of it.
   *
   * So a tag seen once stays offerable for the session. It only ever
   * grows, which is the right direction: the cost is an option that
   * matches nothing, and the list says so plainly when a filter finds
   * nothing. */
  const filterableTags = useMemo(() => {
    const seen = new Map(tags.map((t) => [t.tag_id, t]));
    for (const t of discovered) if (!seen.has(t.tag_id)) seen.set(t.tag_id, t);
    return [...seen.values()];
  }, [tags, discovered]);

  const load = useCallback(async () => {
    try {
      const r = await journalApi.list(scope, {
        // Omitted rather than sent empty: the server treats absent as
        // "no filter", and sending colour="" would be asking for entries
        // whose colour is the empty string, which is nothing.
        ...(filters.search ? { search: filters.search } : {}),
        ...(filters.colour ? { colour: filters.colour } : {}),
        ...(filters.tagIds.length ? { tag_ids: filters.tagIds.join(",") } : {}),
        ...(scope === "team" && filters.authorIds.length ? { author_ids: filters.authorIds.join(",") } : {}),
        ...(filters.pinned ? { pinned: "true" } : {}),
        ...(filters.dateFrom ? { date_from: filters.dateFrom } : {}),
        ...(filters.dateTo ? { date_to: filters.dateTo } : {}),
      });
      const loaded = r.entries || [];
      setEntries(loaded);
      /* Merged by id, and only when something is genuinely new -- the
         same array reference back means no re-render, so this cannot
         loop through filterableTags. */
      setDiscovered((prev) => {
        const seen = new Map(prev.map((t) => [t.tag_id, t]));
        let added = false;
        for (const entry of loaded) {
          for (const t of entry.tags || []) {
            if (!seen.has(t.tag_id)) { seen.set(t.tag_id, t); added = true; }
          }
        }
        return added ? [...seen.values()] : prev;
      });
      setAuthors((prev) => {
        const seen = new Map(prev.map((a) => [a.id, a]));
        let added = false;
        for (const entry of loaded) {
          if (entry.is_shared && entry.author_user_id != null && entry.author_label
              && !seen.has(entry.author_user_id)) {
            seen.set(entry.author_user_id, { id: entry.author_user_id, label: entry.author_label });
            added = true;
          }
        }
        return added ? [...seen.values()] : prev;
      });
    } catch {
      // An empty list and a failed load are different things, and saying
      // "no entries" for the second would suggest somebody's writing had
      // vanished (#197's lesson, and worse here).
      setEntries(null);
      notify("Couldn't load your journal just now", "warning");
    }
  }, [scope, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  /* DEBOUNCED, because `search` changes on every keystroke and this
     load hits the database. Typing "swarm" undebounced is five queries
     of which four are thrown away, and on a slow connection they can
     land out of order -- so the list settles on the results for "swar".
     250ms is below the point most people notice and above the gap
     between keystrokes. */
  useEffect(() => {
    const timer = setTimeout(() => { load(); }, 250);
    return () => clearTimeout(timer);
  }, [load]);

  const loadTags = useCallback(async () => {
    // Best effort: the filter bar hides the tag row when there are none,
    // so a failure here costs a filter rather than the journal.
    try {
      const r = await journalApi.listTags();
      setTags(r.tags || []);
    } catch { /* leave the tag row hidden */ }
  }, []);

  useEffect(() => { loadTags(); }, [loadTags]);

  async function save() {
    if (!draft.trim()) return notify("Write something first", "warning");
    setSaving(true);
    try {
      const created = await journalApi.create({ body: draft.trim() });
      // Says which of the two things happened, because the whole design
      // rests on the default being private.
      notify("Saved to your private journal", "success");
      setDraft("");
      /* The entry is saved by now. If filing it fails, say so rather than
         pretending the whole save failed -- retrying would duplicate it. */
      try {
        if (draftColour) await journalApi.setColour(created.entry_id, draftColour);
        if (draftTagIds.length) await journalApi.setTags(created.entry_id, draftTagIds);
        setDraftColour(null);
        setDraftTagIds([]);
      } catch (err) {
        notify("Saved, but the colour or tags didn't attach — add them from the entry", "warning");
      }
      load();
    } catch (err) {
      notify(err.apiMessage || "Couldn't save that", "danger");
    } finally {
      setSaving(false);
    }
  }

  async function toggleShare(entry) {
    const sharing = !entry.is_shared;
    // Asked ONCE, at the moment it matters, and phrased as what cannot
    // be undone rather than "are you sure". Un-sharing needs no
    // confirmation -- pulling something back is the cautious direction.
    if (sharing) return setPendingShare(entry);
    try {
      await journalApi.setShared(entry.entry_id, sharing);
      notify(sharing ? "Shared with your team" : "Pulled back — no longer in the team journal", "success");
      load();
    } catch (err) {
      notify(err.apiMessage || "Couldn't change that", "danger");
    }
  }

  function startEdit(entry) {
    setEditingId(entry.entry_id);
    setEditDraft(entry.body);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditDraft("");
  }

  async function saveEdit(entry) {
    const text = editDraft.trim();
    /* The server refuses an empty entry ("An empty entry is not an
       entry."). Refusing here too means the user finds out before the
       round trip rather than after it. */
    if (!text || text === entry.body) return;
    setSavingEdit(true);
    try {
      await journalApi.update(entry.entry_id, text);
      cancelEdit();
      await load();
    } catch (err) {
      notify(err.apiMessage || "Couldn't save that change", "danger");
    } finally {
      setSavingEdit(false);
    }
  }

  async function tickBox(entry, line) {
    /* Ticking rewrites the BODY, because that is where the state lives
       (see journalMarkdown.js). So it is an ordinary edit and goes
       through the ordinary update -- which means it stamps
       updated_date and a shared entry shows "edited".

       I ARGUED THE OPPOSITE IN #468 and changed my mind building it.
       The case for exempting was that a shared checklist becomes
       permanently "edited" the first time anyone uses it. But the
       marker's job (#465) is to say the TEXT changed under a teammate,
       and a ticked box IS a change they would want to know about --
       "things to bring next visit" is exactly the entry where a
       teammate cares that something moved. Exempting it would make the
       one control that exists to be honest quietly lie in a specific
       case, which is worse than a date appearing.
    */
    const next = toggleCheckbox(entry.body, line);
    if (next === entry.body) return;   // stale click after an edit
    try {
      await journalApi.update(entry.entry_id, next);
      await load();
    } catch (err) {
      notify(err.apiMessage || "Couldn't update that", "danger");
    }
  }

  async function togglePin(entry) {
    try {
      await journalApi.setPinned(entry.entry_id, !entry.is_pinned);
      await load();
    } catch (err) {
      notify(err.apiMessage || "Couldn't change that", "danger");
    }
  }

  async function setColour(entry, colour) {
    try {
      // Clicking the colour it already has clears it -- the same control
      // sets and unsets, so there is no separate "no colour" button to
      // find and no state you can get stuck in.
      await journalApi.setColour(entry.entry_id, entry.colour === colour ? null : colour);
      await load();
    } catch (err) {
      notify(err.apiMessage || "Couldn't change that", "danger");
    }
  }

  async function toggleTag(entry, tagId) {
    const current = (entry.tags || []).map((t) => t.tag_id);
    const next = current.includes(tagId)
      ? current.filter((t) => t !== tagId)
      : [...current, tagId];
    try {
      await journalApi.setTags(entry.entry_id, next);
      await load();
    } catch (err) {
      notify(err.apiMessage || "Couldn't file that", "danger");
    }
  }

  async function createTag(name) {
    try {
      await journalApi.createTag(name);
      await loadTags();
    } catch (err) {
      notify(err.apiMessage || "Couldn't add that tag", "danger");
    }
  }

  async function retireTag(tagId) {
    try {
      await journalApi.deleteTag(tagId);
      await loadTags();
      /* Drop it from the active filter too. Otherwise the list stays
         narrowed by a tag that is no longer offered anywhere on screen,
         and there is no control left to turn it off with. */
      setFilters((f) => ({ ...f, tagIds: f.tagIds.filter((t) => t !== tagId) }));
    } catch (err) {
      notify(err.apiMessage || "Couldn't retire that tag", "danger");
    }
  }

  async function remove(entry) {
    // ConfirmDialog, not window.confirm (#328). A browser dialog can be
    // switched off -- "prevent this page from creating additional
    // dialogs" makes it return false SILENTLY -- so this guard could
    // simply stop appearing on an entry that is not kept anywhere after
    // it goes. The least controllable dialog in the app was protecting
    // the most irreversible action on the page.
    setPendingDelete(entry);
  }

  async function confirmShare() {
    const entry = pendingShare;
    if (!entry) return;
    try {
      await journalApi.setShared(entry.entry_id, true);
      notify("Shared with your team", "success");
      setPendingShare(null);
      load();
    } catch (err) {
      notify(err.apiMessage || "Couldn't change that", "danger");
    }
  }

  async function confirmDelete() {
    const entry = pendingDelete;
    if (!entry) return;
    try {
      await journalApi.remove(entry.entry_id);
      notify("Deleted", "success");
      setPendingDelete(null);
      load();
    } catch (err) {
      notify(err.apiMessage || "Couldn't delete that", "danger");
    }
  }

  return (
    <div>
      <h1>Journal</h1>
      <p style={{ color: "var(--color-text-muted)", maxWidth: "60ch" }}>
        Whatever is worth remembering and does not fit the forms. Entries are
        private to you — including from the account owner — until you share them.
      </p>

      <Card title="New entry" className="section-spacing">
        {/* NOT a second account selector. "Working in" in the top bar
            already makes this choice, and two controls for one decision
            is its own confusion -- this says what that choice MEANS
            here, which is the part that was missing. */}
        {hasChoice && (
          <p style={{ margin: "0 0 var(--space-2)", color: "var(--color-text-muted)",
                      fontSize: "var(--font-size-sm)" }}>
            Filed under <strong>{currentLabel}</strong>. Change this with
            &ldquo;Working in&rdquo; at the top before you write.
          </p>
        )}
        <Field label="What happened?">
          {(id, aria) => (
            <>
              <textarea
                id={id}
                {...aria}
                rows={4}
                maxLength={JOURNAL_BODY_MAX}
                className="touch-target"
                style={{ width: "100%", resize: "vertical" }}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="The north yard is three weeks ahead of the others this year…"
              />
              <CharacterCount value={draft} max={JOURNAL_BODY_MAX} />
            </>
          )}
        </Field>
        <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap",
                      alignItems: "center", margin: "var(--space-2) 0" }}>
          {JOURNAL_COLOURS.map((c) => (
            <button
              key={c.key}
              type="button"
              aria-label={draftColour === c.key
                ? `Clear ${c.label} from the new entry`
                : `Start the new entry ${c.label}`}
              aria-pressed={draftColour === c.key}
              onClick={() => setDraftColour(draftColour === c.key ? null : c.key)}
              style={{
                width: 18, height: 18, borderRadius: "50%", background: c.css,
                border: draftColour === c.key
                  ? "3px solid var(--color-text)" : "1px solid var(--color-border)",
                cursor: "pointer",
              }}
            />
          ))}
          <CollapsibleChips
            items={tags}
            isSelected={(t) => draftTagIds.includes(t.tag_id)}
            moreLabel="tags"
            limit={JOURNAL_CHIP_LIMIT}
            renderItem={(t) => {
              const on = draftTagIds.includes(t.tag_id);
              return (
                <button
                  key={t.tag_id}
                  type="button"
                  aria-label={`For the new entry: ${on ? "drop" : "use"} the ${t.name} tag`}
                  aria-pressed={on}
                  onClick={() => setDraftTagIds((ids) =>
                    on ? ids.filter((i) => i !== t.tag_id) : [...ids, t.tag_id])}
                  style={{
                    fontSize: "var(--font-size-sm)", borderRadius: "var(--radius-sm)",
                    padding: "2px 8px", cursor: "pointer",
                    border: on ? "1px solid var(--color-text)" : "1px solid var(--color-border)",
                    background: "transparent",
                    color: on ? "var(--color-text)" : "var(--color-text-muted)",
                  }}
                >
                  {t.name}
                </button>
              );
            }}
          />
        </div>
        <Button onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save privately"}
        </Button>
      </Card>

      <div style={{ display: "flex", gap: "var(--space-2)", margin: "var(--space-4) 0 var(--space-3)" }}>
        {TABS.map((tab) => (
          <Button
            key={tab.key}
            size="sm"
            variant={scope === tab.key ? "primary" : "secondary"}
            onClick={() => setScope(tab.key)}
          >
            {tab.label}
          </Button>
        ))}
      </div>

      <Card className="section-spacing">
        <JournalFilters
          value={filters}
          tags={filterableTags}
          authors={scope === "team" ? authors : []}
          onChange={setFilters}
          onManageTags={() => setManagingTags(true)}
        />
      </Card>

      {entries === null ? (
        <Card>
          <p style={{ margin: 0 }}>
            Couldn&apos;t load your journal. Nothing is lost — try again in a moment.
          </p>
        </Card>
      ) : entries.length === 0 ? (
        /* THREE DIFFERENT NOTHINGS, and conflating them is the #197
           lesson a third time. "No entries yet" on a filtered journal
           tells somebody their writing is gone, which on a private
           diary is an alarming thing to read and a support ticket
           either way. A failed load is already handled above; this
           separates "you have written nothing" from "nothing matches
           what you asked for", and the second one offers the way out. */
        filtersActive ? (
          <EmptyState
            title="Nothing matches those filters"
            description="Your entries are all still here — widen or clear the filters above to see them."
            action={<Button variant="secondary" onClick={() => setFilters(NO_FILTERS)}>Clear filters</Button>}
          />
        ) : (
          <EmptyState
            title={scope === "team" ? "Nothing shared yet" : "No entries yet"}
            description={
              scope === "team"
                ? "Entries your team has shared will appear here."
                : "Write the first one above. It stays private until you say otherwise."
            }
          />
        )
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
          {entries.map((entry) => (
            <Card key={entry.entry_id}>
              <div style={{ display: "flex", alignItems: "baseline", gap: "var(--space-2)", flexWrap: "wrap" }}>
                {/* The colour, as a dot on the entry itself. Named for
                    screen readers -- a bare coloured dot is decoration
                    to anyone who cannot see it, and this one carries
                    meaning the user assigned. */}
                {entry.colour && colourOf(entry.colour) && (
                  <span
                    title={colourOf(entry.colour).label}
                    aria-label={`Colour: ${colourOf(entry.colour).label}`}
                    style={{
                      width: 12, height: 12, borderRadius: "50%",
                      background: colourOf(entry.colour).css,
                      display: "inline-block",
                    }}
                  />
                )}
                <strong>{formatDateOnly(entry.entry_date)}</strong>
                {entry.is_pinned && <Badge tone="honey">Pinned</Badge>}
                {/* An entry filed under somebody else's apiary and one
                    filed under your own look identical, and they behave
                    completely differently when shared. scope="mine"
                    filters on the AUTHOR and nothing else, so a helper's
                    journal spans every apiary they work in -- in one
                    list, previously with nothing to tell them apart. */}
                {hasChoice && entry.owner_label && (
                  <Badge tone="neutral">{entry.owner_label}</Badge>
                )}
                {entry.is_shared ? (
                  <Badge tone="success">Shared with the team</Badge>
                ) : (
                  <Badge tone="neutral">Private</Badge>
                )}
                {/* Only on the team view: your own diary does not need
                    your name on every page. */}
                {/* EDITED, AND SAY SO (#465). A teammate can act on a
                    shared entry, so it must not change underneath them
                    with no trace -- the same reasoning that keeps
                    first_shared_at after an entry is pulled back. */}
                {entry.updated_date && (
                  <span style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
                    edited {formatDateOnly(entry.updated_date)}
                  </span>
                )}
                {scope === "team" && !entry.is_mine && entry.author_label && (
                  <span style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
                    {entry.author_label}
                  </span>
                )}
              </div>

              {editingId === entry.entry_id ? (
                <div style={{ margin: "var(--space-2) 0" }}>
                  <Field label="Edit this entry">
                    {(id, aria) => (
                      <>
                        <textarea
                          id={id}
                          {...aria}
                          rows={4}
                          maxLength={JOURNAL_BODY_MAX}
                          className="touch-target"
                          style={{ width: "100%", resize: "vertical" }}
                          value={editDraft}
                          onChange={(e) => setEditDraft(e.target.value)}
                        />
                        {/* The more painful of the two to hit: this text
                            already exists, so a refusal here rejects work
                            the user is extending rather than starting. */}
                        <CharacterCount value={editDraft} max={JOURNAL_BODY_MAX} />
                      </>
                    )}
                  </Field>
                  <div style={{ display: "flex", gap: "var(--space-2)" }}>
                    <Button
                      size="sm"
                      onClick={() => saveEdit(entry)}
                      disabled={savingEdit || !editDraft.trim() || editDraft.trim() === entry.body}
                    >
                      {savingEdit ? "Saving…" : "Save changes"}
                    </Button>
                    <Button size="sm" variant="secondary" onClick={cancelEdit} disabled={savingEdit}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <CollapsibleBody
                  body={entry.body}
                  expanded={expandedIds.has(entry.entry_id)}
                  onToggle={() => setExpandedIds((prev) => {
                    const next = new Set(prev);
                    if (next.has(entry.entry_id)) next.delete(entry.entry_id);
                    else next.add(entry.entry_id);
                    return next;
                  })}
                  /* Only the author gets a working checkbox. V076 makes
                     editing the author's alone and the server enforces
                     it, so offering a tick that will be refused is the
                     kind of lie this page already avoids elsewhere. */
                  onToggleCheckbox={entry.is_mine ? (line) => tickBox(entry, line) : undefined}
                />
              )}

              {/* Only the author can share, pull back or delete -- the
                  database enforces it, and offering buttons that will be
                  refused is its own kind of lie. */}
              {/* FILING CONTROLS, AUTHOR ONLY -- the CONTROLS, not the
                  tags themselves.

                  This block used to be the only place tags were rendered
                  at all, and it is gated on is_mine. That was right under
                  V118, whose rule was that tags never reach the team. It
                  is wrong under V119 (#477), which reversed that: a tag
                  is now exactly as visible as the entry it is on, and
                  app.JournalEntryTags' filter predicate was loosened to
                  match. The database started returning a teammate's tags
                  on a shared entry and this gate dropped them on the
                  floor -- reported as "Main account shared entry shared
                  but tag added from atlarjy account is NOT visible under
                  helper account", and the mirror of it in the other
                  direction.

                  V119 loosened READING only; the BLOCK predicate still
                  compares author_user_id to the session user, because
                  filing is not commenting. So the split is the rule: the
                  read-only row below shows anyone the tags on an entry
                  they can see, and these toggles stay with the author.
                  Rendering the buttons to a teammate would offer an
                  action the database will refuse. */}
              {entry.is_mine && editingId !== entry.entry_id && (
                <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap",
                              alignItems: "center", marginBottom: "var(--space-2)" }}>
                  {JOURNAL_COLOURS.map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      /* NAMED APART FROM THE FILTER BAR, which renders a
                         swatch for the same colour. Two buttons called
                         "Amber" on one page are indistinguishable to
                         anyone navigating by name, and they do opposite
                         things -- one narrows the list, one changes the
                         entry. The name also says which way it goes. */
                      aria-label={entry.colour === c.key
                        ? `Clear ${c.label} from this entry`
                        : `Mark this entry ${c.label}`}
                      aria-pressed={entry.colour === c.key}
                      onClick={() => setColour(entry, c.key)}
                      style={{
                        width: 18, height: 18, borderRadius: "50%", background: c.css,
                        border: entry.colour === c.key
                          ? "3px solid var(--color-text)"
                          : "1px solid var(--color-border)",
                        cursor: "pointer",
                      }}
                    />
                  ))}
                  {/*
                    * TWO GROUPS, BOTH COLLAPSING (#497).
                    *
                    * Reported 6 Oct: "the available message tags hid in a
                    * '+N tags' link ... but the assigned tags do NOT
                    * collapse even if I assign 15 tags."
                    *
                    * Working as designed, and the design did not scale.
                    * This was ONE CollapsibleChips over every tag, with
                    * the assigned ones marked `isSelected` -- and that
                    * component deliberately never hides a selected chip,
                    * so fifteen assigned tags all stayed on screen and
                    * only the unused ones collapsed. The wall of chips
                    * #473 set out to remove came back through the half
                    * nobody had measured.
                    *
                    * So: what is ON the entry, then what can be ADDED,
                    * each collapsing on its own. `isSelected` is now a
                    * constant within each group, since membership is
                    * what SPLITS them rather than something to mark
                    * inside one list.
                    *
                    * Splitting also answers the second half of the
                    * report -- "we should separate the assigned tags from
                    * the available tags" -- which is the same call #477
                    * made for the filter bar.
                    */}
                  {(entry.tags || []).length > 0 && (
                    <>
                      <span style={{ fontSize: "var(--font-size-sm)",
                                     color: "var(--color-text-muted)" }}>
                        On this entry:
                      </span>
                      <CollapsibleChips
                        items={entry.tags}
                        isSelected={() => false}
                        moreLabel="tags"
            limit={JOURNAL_CHIP_LIMIT}
                        renderItem={(t) => (
                          <button
                            key={`on-${t.tag_id}`}
                            type="button"
                            aria-label={`Remove tag ${t.name}`}
                            aria-pressed
                            onClick={() => toggleTag(entry, t.tag_id)}
                            style={{
                              fontSize: "var(--font-size-sm)",
                              borderRadius: "var(--radius-sm)",
                              padding: "2px 8px",
                              border: "1px solid var(--color-text)",
                              background: "var(--color-surface-alt, transparent)",
                              cursor: "pointer",
                            }}
                          >
                            {t.name}
                          </button>
                        )}
                      />
                    </>
                  )}
                  {/* What is left to add. Collapses on its own count, so a
                      long vocabulary cannot bury the entry's own tags. */}
                  <CollapsibleChips
                    items={tags.filter(
                      (t) => !(entry.tags || []).some((x) => x.tag_id === t.tag_id))}
                    isSelected={() => false}
                    moreLabel="tags"
            limit={JOURNAL_CHIP_LIMIT}
                    /* No on/off state to carry any more: this list is
                       filtered to tags NOT on the entry, so the old
                       `aria-pressed={on}` could only ever be false. A flag
                       that cannot change is worse than no flag -- it
                       reads as a state somebody might see. */
                    renderItem={(t) => (
                      <button
                        key={t.tag_id}
                        type="button"
                        aria-label={`Add tag ${t.name}`}
                        aria-pressed={false}
                        onClick={() => toggleTag(entry, t.tag_id)}
                        style={{
                          fontSize: "var(--font-size-sm)", borderRadius: "var(--radius-sm)",
                          padding: "2px 8px", cursor: "pointer",
                          border: "1px solid var(--color-border)",
                          background: "transparent",
                          color: "var(--color-text-muted)",
                        }}
                      >
                        {t.name}
                      </button>
                    )}
                  />
                </div>
              )}

              {/* SOMEBODY ELSE'S TAGS, READ ONLY (V119, #477).

                  The other half of the split above. A shared entry's tags
                  belong to its author and cannot be changed here, but
                  they are the only way to tell what a teammate's entry is
                  ABOUT at a glance -- which was the whole objection that
                  reversed V118: "If the users can't tag entries then the
                  other team members can't filter or search on those tags
                  in the shared entries."

                  Spans, not buttons. A chip that looks identical to the
                  author's toggle but does nothing when pressed is worse
                  than no chip: it reads as the app being broken rather
                  than as somebody else's label. Muted and unpressable
                  says "this is information" without needing a caption.

                  Collapses on the same rule as the author's row, so a
                  teammate who files heavily does not bury the entry under
                  its own labels (#473).

                  NOTE ON FILTERING, because the objection above asks for
                  it and this does not deliver all of it: the filter bar
                  still offers only your OWN tag vocabulary, which is a
                  deliberate decision recorded in JournalFilters.jsx. The
                  tags are visible here; filtering BY a teammate's tag is
                  a separate call that has not been made. */}
              {!entry.is_mine && (entry.tags || []).length > 0 && (
                <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap",
                              alignItems: "center", marginBottom: "var(--space-2)" }}>
                  <CollapsibleChips
                    items={entry.tags}
                    isSelected={() => false}
                    moreLabel="tags"
            limit={JOURNAL_CHIP_LIMIT}
                    renderItem={(t) => (
                      <span
                        key={t.tag_id}
                        style={{
                          fontSize: "var(--font-size-sm)", borderRadius: "var(--radius-sm)",
                          padding: "2px 8px",
                          border: "1px solid var(--color-border)",
                          background: "transparent",
                          color: "var(--color-text-muted)",
                        }}
                      >
                        {t.name}
                      </span>
                    )}
                  />
                </div>
              )}

              {/* THE WHOLE ROW GOES WHILE EDITING (#474).
                  
                  Save changes and Cancel render just above this, so
                  Delete sat one row below Cancel -- the fastest way to
                  abandon an edit directly above the fastest way to lose
                  the entry, and closer still on a touch screen.
                  
                  Hidden rather than rearranged, because none of these
                  is a sensible thing to do mid-edit: pulling back or
                  deleting an entry you are part-way through rewriting
                  is a question with no good answer. Finish or cancel
                  first. */}
              {entry.is_mine && editingId !== entry.entry_id && (
                <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
                  <Button size="sm" variant="secondary" onClick={() => togglePin(entry)}>
                    {entry.is_pinned ? "Unpin" : "Pin"}
                  </Button>
                  {/* disabled rather than hidden: an edit open on ANOTHER
                      entry is the reason, and a button that vanishes
                      explains nothing. */}
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => startEdit(entry)}
                    disabled={editingId !== null}
                  >
                    Edit
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => toggleShare(entry)}>
                    {entry.is_shared ? "Pull back" : "Share with team"}
                  </Button>
                  <Button size="sm" variant="danger" onClick={() => remove(entry)}>
                    Delete
                  </Button>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
      {/* confirmLabel is "Delete entry", deliberately not "Delete": the row
          button that opens this already says Delete, and two controls with
          one word is a coin toss for anybody moving by keyboard or hearing
          it read out. */}
      <ConfirmDialog
        open={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        title="Delete this entry?"
        confirmLabel="Delete entry"
        danger
      >
        <p>It is not kept anywhere after this.</p>
      </ConfirmDialog>

      <JournalTagManager
        open={managingTags}
        tags={tags}
        onClose={() => setManagingTags(false)}
        onCreate={createTag}
        onRetire={retireTag}
      />

      <ConfirmDialog
        open={Boolean(pendingShare)}
        onClose={() => setPendingShare(null)}
        onConfirm={confirmShare}
        title={hasChoice && pendingShare?.owner_label
          ? `Share this with ${pendingShare.owner_label}'s team?`
          : "Share this with your team?"}
        confirmLabel="Share"
      >
        {/* NAMES THE TEAM (#471). "Share with the team" is a different
            question from "share with atlarjy's team" when you help
            three people, and the entry's own account decides which --
            not whichever one happens to be selected right now. */}
        {hasChoice && pendingShare?.owner_label && (
          <p>
            This entry is filed under <strong>{pendingShare.owner_label}</strong>,
            so it goes to that team.
          </p>
        )}
        {/* What cannot be undone, rather than "are you sure". */}
        <p>
          You can pull it back later, but anyone who has already read it will
          have read it.
        </p>
      </ConfirmDialog>

    </div>
  );
}
